import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon, ArrowRightIcon, IconButton, Tooltip } from "@activecollab/components";
import PropTypes from "prop-types";
import { useDiscussionNavigation } from "~/repository";
import Angie from "~/utils/angie";

export const Navigation = ({ projectId, discussionId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading, isFetched } = useDiscussionNavigation(projectId, discussionId);

  const { next, prev } = data ?? {};

  const handlePrev = useCallback(() => {
    if (prev) {
      Angie.logger.info("Discussion navigation button clicked.", {
        direction: "prev",
        navigation_source: "single",
        navigation_type: "discussion",
      });
      navigate(`/projects/${projectId}/discussions/${prev}`);
    }
  }, [navigate, prev, projectId]);

  const handleNext = useCallback(() => {
    if (next) {
      Angie.logger.info("Discussion navigation button clicked.", {
        direction: "next",
        navigation_source: "single",
        navigation_type: "discussion",
      });
      navigate(`/projects/${projectId}/discussions/${next}`);
    }
  }, [navigate, next, projectId]);

  const showPrevNext = Boolean(prev || next);
  const loading = !isFetched || isLoading;

  if (!showPrevNext && !loading) return null;

  return (
    <span className="tw-pr-3 tw-mr-3 tw-border-0 tw-border-solid tw-border-border-primary tw-border-r tw-inline-flex">
      <Tooltip
        disable={loading}
        title={prev ? t("Previous Discussion") : t("You are at beginning of the list")}
      >
        <div>
          <IconButton
            disabled={!prev || loading}
            onClick={handlePrev}
            variant="text gray"
            className="tw-mr-2"
          >
            <ArrowLeftIcon />
          </IconButton>
        </div>
      </Tooltip>
      <Tooltip
        disable={loading}
        title={next ? t("Next Discussion") : t("You have reached the end of the list")}
      >
        <div>
          <IconButton
            disabled={!next || loading}
            onClick={handleNext}
            variant="text gray"
          >
            <ArrowRightIcon />
          </IconButton>
        </div>
      </Tooltip>
    </span>
  );
};

Navigation.propTypes = {
  projectId: PropTypes.number.isRequired,
  discussionId: PropTypes.number.isRequired,
};
